import { z } from "zod";
import type { ExpenseCard } from "@/lib/expenses/cards";
import { EXPENSE_MAX_LINES } from "@/lib/expenses/limits";
import { type ApprovalStatus, isWholeCents, type PaymentStatus } from "@/lib/expenses/rules";
import { SCAN_MAX_BASE64, SCAN_MAX_FILES, type ScanResult } from "@/lib/expenses/scan";
import { isIsoDate } from "@/lib/time/dates";
import { teamIdSchema } from "@/schemas/team";

export const EXPENSE_DESCRIPTION_MAX = 200;
export const EXPENSE_LINE_MAX = 200;
export const EXPENSE_REJECT_MAX = 500;
const RECEIPT_NAME_MAX = 150;
/** 6 MB as base64. */
const RECEIPT_BASE64_MAX = 8_400_000;

const isoDate = z.string().refine(isIsoDate, { message: "Pick a date" });

const expenseLineInputSchema = z.strictObject({
  id: teamIdSchema.optional(),
  date: isoDate,
  amount: z
    .number("Enter an amount")
    .positive("Enter an amount above 0")
    .max(1_000_000, "Enter a smaller amount")
    .refine(isWholeCents, {
      message: "Use whole cents",
    }),
  description: z.string().trim().min(1, "Describe each line").max(EXPENSE_LINE_MAX),
});

export const expenseSaveInputSchema = z.strictObject({
  id: teamIdSchema.optional(),
  typeId: teamIdSchema,
  periodId: teamIdSchema,
  /** Only for a Department POs claim; the server re-reads it. */
  poId: teamIdSchema.optional(),
  description: z.string().trim().min(1, "Add a description").max(EXPENSE_DESCRIPTION_MAX),
  lines: z.array(expenseLineInputSchema).max(EXPENSE_MAX_LINES),
});
export type ExpenseSaveInput = z.infer<typeof expenseSaveInputSchema>;

export const expenseIdInputSchema = z.strictObject({ id: teamIdSchema });
export type ExpenseIdInput = z.infer<typeof expenseIdInputSchema>;

export const expenseRejectInputSchema = z.strictObject({
  id: teamIdSchema,
  note: z.string().trim().min(1, "Give a reason").max(EXPENSE_REJECT_MAX),
});
export type ExpenseRejectInput = z.infer<typeof expenseRejectInputSchema>;

export const receiptInputSchema = z.strictObject({
  lineId: teamIdSchema,
  fileName: z.string().trim().min(1).max(RECEIPT_NAME_MAX),
  data: z.string().min(1).max(RECEIPT_BASE64_MAX),
});
export type ReceiptInput = z.infer<typeof receiptInputSchema>;

export const receiptScanInputSchema = z.strictObject({
  files: z
    .array(
      z.strictObject({
        fileName: z.string().trim().min(1).max(RECEIPT_NAME_MAX),
        data: z.string().min(1).max(RECEIPT_BASE64_MAX),
      }),
    )
    .min(1, "Pick a receipt")
    .max(SCAN_MAX_FILES, `Scan up to ${SCAN_MAX_FILES} files at a time`)
    .refine((files) => files.reduce((sum, f) => sum + f.data.length, 0) <= SCAN_MAX_BASE64, {
      message: "Those files are too large to scan together",
    }),
});
export type ReceiptScanInput = z.infer<typeof receiptScanInputSchema>;

export type ExpenseScanResult = ScanResult;

export const expenseYearQuerySchema = z.object({
  year: z.coerce.number().int().min(2020).max(2100),
});

export interface ExpenseClaimRow {
  id: string;
  typeId: string;
  typeName: string;
  typeEnum: string;
  periodId: string;
  monthName: string;
  approval: ApprovalStatus | null;
  payment: PaymentStatus | null;
  description: string;
  dateSubmitted: string | null;
  estPaidOn: string | null;
  total: number;
  /** Pending with nobody able to decide it (spec 7). */
  noApprover: boolean;
  /** "#130 Amazon", or null. */
  po: string | null;
}

export interface ExpensesView {
  year: number;
  today: string;
  /** Other years with claims, newest first, so a year's list never hides them silently. */
  otherYears: number[];
  cards: ExpenseCard[];
  claims: ExpenseClaimRow[];
}

interface ExpenseOption {
  id: string;
  name: string;
}

export interface ExpensePoOption {
  id: string;
  label: string;
  department: string;
  isDemand: boolean;
  number: number | null;
  periodId: string | null;
}

export interface ExpenseOptions {
  types: Array<ExpenseOption & { typeEnum: string }>;
  months: Array<ExpenseOption & { yearId: string | null }>;
  /** Department and Demand POs; the form narrows them to the month's year. */
  pos: ExpensePoOption[];
  defaultMonthId: string | null;
  /** Receipt scanning is set up on this server. */
  scanEnabled: boolean;
}

interface ExpenseLineView {
  id: string;
  date: string | null;
  amount: number;
  description: string | null;
}

export interface ReceiptView {
  id: string;
  lineId: string;
  fileName: string;
  size: number;
}

interface ExpenseNoteView {
  id: string;
  note: string;
  isRejection: boolean;
  addedBy: string | null;
  addedOn: string | null;
}

export interface ExpenseDetail extends ExpenseClaimRow {
  claimantName: string | null;
  department: string | null;
  poId: string | null;
  approvedOn: string | null;
  isMine: boolean;
  canEdit: boolean;
  canDecide: boolean;
  rejectionNote: string | null;
  lines: ExpenseLineView[];
  receipts: ReceiptView[];
  notes: ExpenseNoteView[];
}

export interface ExpenseApprovalRow {
  id: string;
  claimantName: string;
  typeName: string;
  department: string | null;
  po: string | null;
  monthName: string;
  description: string;
  dateSubmitted: string | null;
  total: number;
  /** Decided here only because the claimant is the department's sole owner. */
  asBackup: boolean;
}

export interface ExpenseApprovalsView {
  rows: ExpenseApprovalRow[];
}

export interface ExpensePendingView {
  count: number;
  /** An active department owner, or the backup approver with a claim waiting. */
  canApprove: boolean;
}

export interface ExpenseSaveResult {
  id: string;
  /** The saved line ids, in the order the lines were sent. */
  lineIds: string[];
}

export interface ExpenseStatusResult {
  id: string;
  status: ApprovalStatus;
}

export interface ReceiptResult {
  id: string;
}
